
import { mapGetters, mapActions } from "vuex";
import { isEqual } from "lodash";
import useProductMixin from "~/mixins/useProductMixin";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents";
import facebookPixelEvents from "~/mixins/facebookPixelEvents";
import useEvents from "~/mixins/useEvents";
import { getProductImages, mergeImages, DataURIToBlob } from "~/utils";
import UiLazyImg from "~/components/UI/UiLazyImg.vue";

export default {
  name: "RelatedProductCardPDP",
  components: {
    UiLazyImg,
  },
  mixins: [
    useProductMixin,
    googleAnalyticsEvents,
    facebookPixelEvents,
    useEvents,
  ],
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      productData: null,
      addingToCart: false,
    };
  },
  computed: {
    ...mapGetters({
      getWishList: "wishlist/getWishList",
    }),
    productImage() {
      const images = getProductImages(this.data);
      return images?.[0]?.url || this.data.image || "";
    },
    isInWishlist() {
      return this.getWishList.some((el) =>
        isEqual(
          {
            sku: el.sku,
            engraving: el.engraving,
          },
          { engraving: {}, sku: this.productSKU }
        )
      );
    },
    hasDiscount() {
      return !!this.discountValue || !!this.productStartAtPrice;
    },
    discountPercent() {
      if (this.discountValue && this.productPrice) {
        const percent = Math.round(
          ((this.productPrice - this.discountValue) / this.productPrice) * 100
        );
        return percent;
      }
      if (this.productStartAtPrice && this.productPrice) {
        const percent = Math.round(
          ((this.productStartAtPrice - this.productPrice) /
            this.productStartAtPrice) *
            100
        );
        return percent;
      }
      return null;
    },
    isOutOfStock() {
      return this.isConfigurationOutOfStockPlp;
    },
  },
  watch: {
    data: {
      handler(val) {
        this.productData = val;
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      setWishlist: "wishlist/setWishlist",
    }),
    toggleWishlist() {
      this.onWishlist();
    },
    async onWishlist() {
      if (!this.data.default_sku) {
        return;
      }

      const payload = {
        value: {
          engraving: {},
          images: this.productImage,
          sku: this.productSKU,
        },
      };

      try {
        const wishlistUniqueId = this.$store.getters.getWishListId;
        let wishlist = JSON.parse(JSON.stringify(this.getWishList));
        if (!wishlist) {
          wishlist = [];
        }

        const isExist = wishlist.find((el) =>
          isEqual(
            { sku: el.sku, engraving: el.engraving },
            { engraving: payload.value.engraving, sku: payload.value.sku }
          )
        );

        const wishlistEventData = {
          product_price: this.productPrice,
          product_title: this.productTitle,
          product_id: this.data.id,
          configuration_id: this.productSKU,
          product_configuration_id: this.data?.default_config?.id,
        };

        if (!isExist) {
          wishlist.push({
            ...payload.value,
            slug: this.data.slug,
            title: this.productTitle,
            price: this.productPrice,
            embedded: this.data.embedded,
            translations: this.data.translations || [],
            vendor_model: this.data.vendor_model,
            id: this.data.id,
            collections_ids: this.data.collections_ids || [],
            start_at_cost: this.productPrice,
            product_id: this.data.id,
            configuration_id: this.productSKU,
            product_configuration_id: this.data?.default_config?.id,
          });

          this.gaAddToWishlistEvent(wishlistEventData);
          this.fbAddToWishlistEvent(wishlistEventData);
        } else {
          wishlist = wishlist.filter(
            (el) =>
              !isEqual(
                { sku: el.sku, engraving: el.engraving },
                { engraving: payload.value.engraving, sku: payload.value.sku }
              )
          );
          this.gaRemoveFromWishlistEvent(wishlistEventData);
          this.fbRemoveFromWishlistEvent(wishlistEventData);
        }

        const { data } = await this.$api.wishlist.addItem({
          unique_id: wishlistUniqueId,
          baggage: wishlist,
        });

        const eventPayload = {
          product_id: this.data.id,
          configuration_id: this.productSKU,
          product_sku: this.data.vendor_model,
          product_price: this.productPrice,
          collection_id: this.data.collections_ids?.[0] || "",
          product_configuration_id: this.data?.default_config?.id,
        };

        if (!isExist) {
          this.addToFavoritesEvent(eventPayload);
        } else {
          this.removeFromFavoritesEvent(eventPayload);
        }

        this.setWishlist(data.data.baggage);
      } catch (err) {
      }
    },
    async addToCart() {
      if (this.addingToCart || !this.productSKU || this.data.embedded) {
        return;
      }
      this.addingToCart = true;
      try {
        const images = getProductImages(this.data);
        const imageIdList =
          images?.filter((item) => item?.id).map((item) => item.id) || [];
        const imageId = imageIdList?.[0];

        let cartImage = null;
        if (!imageId) {
          cartImage = await mergeImages([
            {
              src: this.productImage || "/images/no-image.webp",
            },
          ]);
          cartImage = DataURIToBlob(cartImage);
        }

        const options = {};

        const formData = new FormData();
        formData.append("cart_unique_id", this.$store.getters.getCartId);
        formData.append("sku", this.productSKU);
        formData.append("vendor_model", this.data?.vendor_model || "");
        formData.append("quantity", "1");
        formData.append("options", JSON.stringify(options));

        if (imageId) {
          formData.append("image_id", imageId);
          imageIdList.forEach((id) => {
            formData.append("images_ids[]", id);
          });
        } else {
          formData.append("image", cartImage);
        }

        const { data } = await this.$api.cart.setCartItem(formData);
        await this.$store.dispatch("cart/setCartInfo", data.data);

        this.$store.commit("general/TOGGLE_CART_DRAWER", true);

        const configInfo = {
          configuration_id: this.productSKU,
          base_cost: this.productPrice,
        };

        this.gaAddToCartEvent(this.data, configInfo);
        this.fbAddToCartEvent(this.data, configInfo);
        this.addToCartEvent({
          product_id: this.data.id,
          configuration_id: this.productSKU,
          product_sku: this.data.vendor_model,
          product_price: this.productPrice,
        });
      } catch (error) {
      } finally {
        this.addingToCart = false;
      }
    },
  },
};
